import apiClient from '../connections/api'

const progressHelper = (onProgress) => {
    return (response) => {
        if (!response.body) return response

        let loaded = 0
        const contentLength = response.headers.get('content-length')
        const total = !contentLength ? -1 : parseInt(contentLength, 10)

        return new Response(
            new ReadableStream({
                start (controller) {
                    const reader = response.body.getReader()
                    return read()

                    function read () {
                        return reader
                            .read()
                            .then(({ done, value }) => {
                                if (done) {
                                    controller.close()
                                    return
                                }
                                loaded += value.byteLength
                                onProgress({ loaded, total })
                                controller.enqueue(value)
                                return read()
                            })
                            .catch((error) => {
                                console.error(error)
                                controller.error(error)
                            })
                    }
                }
            })
        )
    }
}

export const editReport = (report) => {
    return apiClient.patch('/reports/editReport', report)
}

export const editReportById = (report) => {
    return apiClient.post('/reports/editReportById', { report })
}

export const deleteReport = (id) => {
    return apiClient.post('/reports/deleteReport', { id })
}

export const getAllReports = (setReportsProgress, serverUrl) => {
    const request = new Request(`${serverUrl}/reports/getAllReports`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
    return fetch(request).then(progressHelper(setReportsProgress))
}

export const getAllReportsbySite = (site, setReportsProgress, serverUrl) => {
    const request = new Request(`${serverUrl}/reports/getAllReportsbySite`, {
        method: 'POST',
        body: JSON.stringify({ site: site }),
        headers: { 'Content-Type': 'application/json' }
    })
    return fetch(request).then(progressHelper(setReportsProgress))
}

export const getReportByGuide = (guideNumber) => {
    return apiClient.post('/reports/getReportByGuide', { guideNumber })
}

export const getReportByType = (type) => {
    return apiClient.post('/reports/getReportByType', { type })
}

export const getReportByState = (state, reportType) => {
    return apiClient.post('/reports/getReportByState', { state, reportType })
}

export const getReportByStateAndSite = (state, reportType, site) => {
    return apiClient.post('/reports/getReportByStateAndSite', {
        state,
        reportType,
        site
    })
}

export const getReportsByUser = (userId) => {
    return apiClient.post('/reports/getReportsByUser', { userId })
}

export const getReportByEquid = (equid) => {
    return apiClient.get(`/reports/getReportByEquid?equid=${equid}`)
}

export const getReportByIdpm = (idpm) => {
    return apiClient.post('/reports/getReportByIdpm', { idpm })
}

export const getReportsByDateRange = (dateInit, dateEnd, reportType) => {
    return apiClient.post('/reports/getReportsByDateRange', {
        dateInit,
        dateEnd,
        reportType
    })
}

export const getReportsByDateRangeAndSite = (
    dateInit,
    dateEnd,
    reportType,
    site
) => {
    return apiClient.post('/reports/getReportsByDateRangeAndSite', {
        dateInit,
        dateEnd,
        reportType,
        site
    })
}

export const getTotalReportsToIndex = () => {
    return apiClient.get('/reports/getTotalReportsToIndex')
}

export const getReporstWithBacklogsByMachine = (equid) => {
    return apiClient.get(`/reports/getReporstWithBacklogsByMachine/${equid}`)
}

export const findBacklogsInReport = (reportId) => {
    return apiClient.get(`/reports/findBacklogsInReport/${reportId}`)
}

export const getEffectiveHours = (id) => {
    return apiClient.get(`/reports/${id}/effective-hours`)
}

export const reportesPaginados = async (
    tipo,
    desde,
    maximo,
    obra,
    tipoReporte,
    sortField,
    sortOrder,
    deleted = false
) => {
    const response = await apiClient.post('/reports/reportesPaginados', {
        tipo,
        desde,
        maximo,
        obra,
        tipoReporte,
        sortField,
        sortOrder,
        deleted
    })
    return response.data
}

export const restoreReport = async (id) => {
    const response = await apiClient.post('/reports/restoreReport', { id })
    return response.data
}

export const reportesPaginadosFiltrados = async (
    tipo,
    desde,
    maximo,
    obra,
    tipoReporte,
    global,
    value,
    tipoDeBusqueda,
    sortField,
    sortOrder
) => {
    const response = await apiClient.post('/reports/reportesPaginadosFiltrados', {
        tipo,
        desde,
        maximo,
        obra,
        tipoReporte,
        global,
        value,
        tipoDeBusqueda,
        sortField,
        sortOrder
    })
    return response.data
}

export const leerReportesCalendario = async (mes, ano) => {
    const response = await apiClient.post('/reports/leerReportesCalendario', {
        mes,
        ano
    })
    return response.data
}

export const findMyAssignations = async () => {
    return await apiClient.get('/reports/getReportsByMyAssignations', {
        suppressGlobalErrorHandler: true
    })
}

export const newReport = (report) => {
    return apiClient.post('/reports/createReport', report)
}

export const createManualReport = (report) => {
    return apiClient.post('/reports/manual', report)
}

export const getReportById = async (reportId) => {
    return await apiClient.get(`/reports/getReportById/${reportId}`)
}

export const getReportByIndex = async (index) => {
    return await apiClient.get(`/reports/getReportByIndex?index=${index}`)
}

export const getDetailedReport = async (otIndex) => {
    return await apiClient.get(`/reports/details/${otIndex}`)
}

export const getPdfUrl = async (reportId) => {
    return await apiClient.get(`/reports/pdf-url/${reportId}`)
}

export const getTechnicalDocUrl = async (reportId) => {
    if (!reportId) {
        throw new Error(
            '[reports.routes] reportId is required for getTechnicalDocUrl'
        )
    }
    return await apiClient.get(
        `/reports/${encodeURIComponent(reportId)}/technical-doc`
    )
}

export const assignReport = (reportId, assignmentData) => {
    return apiClient.patch(`/reports/${reportId}/assign`, assignmentData)
}

export const getKPIs = async (params) => {
    return await apiClient.get('/reports/kpi', { params })
}

export const checkOpenReportForUser = async () => {
    const response = await apiClient.get('/reports/check-open-report')
    return response.data
}

export const migrateReportsToSite = async (
    fromSiteId,
    toSiteId,
    toSiteIdObra
) => {
    const response = await apiClient.post('/reports/migrate-to-site', {
        fromSiteId,
        toSiteId,
        toSiteIdObra
    })
    return response.data
}

export const getReportsSummary = async (
    obra,
    skip = 0,
    limit = 30,
    isAutomatic = '',
    config = {}
) => {
    const response = await apiClient.get(
        `/reports/resumen/${obra}?skip=${skip}&limit=${limit}&isAutomatic=${isAutomatic}`,
        config
    )
    return response.data
}

export const getNumerosReportes = async (obra) => {
    const response = await apiClient.get(
        `/reports/leerReportesNumerosSegunEstado/${obra}`,
        { suppressGlobalErrorHandler: true }
    )
    return response.data
}

export const findAsignationRevisors = async (site) => {
    return await apiClient.get('/reports/getReportsRevisors?site=' + site)
}

const reportsRoutes = {
    editReport,
    editReportById,
    deleteReport,
    getAllReports,
    getAllReportsbySite,
    getReportByGuide,
    getReportByType,
    getReportByState,
    getReportByStateAndSite,
    getReportsByUser,
    getReportByEquid,
    getReportByIdpm,
    getReportsByDateRange,
    getReportsByDateRangeAndSite,
    getTotalReportsToIndex,
    getReporstWithBacklogsByMachine,
    findBacklogsInReport,
    getEffectiveHours,
    reportesPaginados,
    restoreReport,
    reportesPaginadosFiltrados,
    leerReportesCalendario,
    findMyAssignations,
    newReport,
    getReportById,
    getReportByIndex,
    getDetailedReport,
    getPdfUrl,
    getTechnicalDocUrl,
    assignReport,
    getKPIs,
    checkOpenReportForUser,
    migrateReportsToSite,
    getNumerosReportes,
    findAsignationRevisors,
    getReportsSummary
}

export default reportsRoutes
